import React, { useContext, useState } from 'react';
import Styled, { ThemeContext } from 'styled-components';
import { gql, useQuery } from '@apollo/client';
import { Trans } from '@lingui/react';
import { t } from '@lingui/macro';
import Media from 'react-media';
import select from '@components/form/Select';
import Languages from '@organisms/Languages';
import { Container, Box, Flex } from '@components/layout/Grid';
import Icon from '@components/Icon';
import { Body, Header, Modal } from '@organisms/modals/base';
import { Option, ProgramCategory, Query } from '@gql/generated';
import { PROGRAM_THEMES } from '@lib/constants';

const Filters = Styled.div`
  position: sticky;
  top: 0;
  padding: 10px 20px;
  background-color: #F0F5FB;
  box-shadow: 2px 2px 4px #09080838;
  z-index: 9;
`;

const Select = Styled(select)`
  background-color: ${({ theme }) => theme.colors.neutrals.white};
  

  .MuiInputBase-root {
    .MuiSelect-select {
      font-size: 13px;
      text-transform: capitalize;
      border: solid 1px #dddddd;
      border-radius: 5px;
      padding: 10px 35px 10px 14px;
    }

    .MuiSelect-icon {
      right: 5px;
    }
  }
`;

const MobileFilterOpen = Styled.button`
  border: 1px solid lightgrey;
  border-radius: 3px;
  padding: 12px 20px;
  margin: 0 auto;
  background-color: white;
`;

const PROGRAM_INSTRUCTORS_QUERY = gql`
  query programInstructors($option: Option = program_instructors_only) {
    instructors(perPage: 100, page: 1, option: $option) {
      data {
        name
        slug
      }
    }
  }
`;

const PROGRAM_THEMES_QUERY = gql`
  query programThemes($category: ProgramCategory = programs) {
    programThemes(category: $category)
  }
`;

const sortOptions = [
  {
    label: <Trans id="programs_2021.listings_page.filters.sort_by" />,
    value: '-1',
  },
  {
    label: (
      <Trans id="programs_2021.listings_page.filters.sort_alphabetically" />
    ),
    value: 'alphabetical',
  },
  {
    label: <Trans id="programs_2021.listings_page.filters.sort_duration" />,
    value: 'duration',
  },
  {
    label: <Trans id="programs_2021.listings_page.filters.sort_new" />,
    value: 'newest_first',
  },
];

const OPTIONS = {
  [ProgramCategory['Programs']]: Option['ProgramInstructorsOnly'],
  [ProgramCategory['Courses']]: Option['CourseInstructorsOnly'],
  [ProgramCategory['Healthcare']]: 'healthcare_instructors_only',
};

const FilterInputs = ({
  isMobile,
  languages = ['se', 'en', 'no', 'fi'],
  instructor,
  sortBy,
  theme,
  programCategory,
  onLanguageChange,
  onInstructorChange,
  onSortChange,
  onThemeChange,
}) => {
  const { data: instructorsData, error: instructorsError } = useQuery<{
    instructors: Query['instructors'];
  }>(PROGRAM_INSTRUCTORS_QUERY, {
    variables: { option: OPTIONS[programCategory] },
  });
  //
  const { data: ThemesData } = useQuery<{
    programThemes: Query['programThemes'];
  }>(PROGRAM_THEMES_QUERY, { variables: { category: programCategory } });

  const mt = isMobile ? 20 : 0;
  const direction = isMobile ? 'column' : 'row';

  return (
    <>
      <Flex flexDirection={direction}>
        <Box mr={10} mt={mt}>
          <Select
            name="sort"
            options={sortOptions}
            valueKey="value"
            displayTextKey="label"
            selectedValue={sortBy === null ? '-1' : sortBy}
            onChange={e => {
              const value = e.target.value;
              onSortChange(value === '-1' ? null : value);
            }}
          />
        </Box>
        {!instructorsError ? (
          <Box mr={10} mt={mt}>
            <Select
              name="instructors"
              options={[
                {
                  label: (
                    <Trans id="programs_2021.listings_page.filters.instructors" />
                  ),
                  value: '-1',
                },
                ...(instructorsData?.instructors.data || []).map(
                  instructor => ({
                    label: instructor.name,
                    value: instructor.slug,
                  })
                ),
              ]}
              valueKey="value"
              displayTextKey="label"
              selectedValue={instructor === null ? '-1' : instructor}
              onChange={e => {
                const instructor = (e.target as HTMLSelectElement).value;
                onInstructorChange(instructor === '-1' ? null : instructor);
              }}
            />
          </Box>
        ) : null}
        <Box mr={10} mt={mt}>
          <Select
            name="theme"
            options={[
              {
                label: (
                  <Trans id="programs_2021.listings_page.filters.kind_theme" />
                ),
                value: '-1',
              },
              ...(ThemesData?.programThemes || []).map(theme => ({
                label: t({ id: PROGRAM_THEMES[theme] }),
                value: theme,
              })),
            ]}
            valueKey="value"
            displayTextKey="label"
            selectedValue={theme === null ? '-1' : theme}
            onChange={e => {
              const value = e.target.value;
              onThemeChange(value === '-1' ? null : value);
            }}
          />
        </Box>
      </Flex>
      <Flex
        mt={mt}
        flex={1}
        justifyContent={isMobile ? 'center' : 'flex-end'}
        alignItems="center"
      >
        <span>
          {programCategory === ProgramCategory['Courses'] ? (
            <Trans id="courses_2021.listings_page.filters.languages" />
          ) : (
            <Trans id="programs_2021.listings_page.filters.languages" />
          )}
          :
        </span>
        <Languages
          languages={languages}
          onChange={onLanguageChange}
          enabledLanguages={['se', 'en', 'no', 'fi']}
        />
      </Flex>
    </>
  );
};

const FilterModal = ({
  languages,
  instructor,
  sortBy,
  theme,
  programCategory,
  onLanguageChange,
  onInstructorChange,
  onSortChange,
  onThemeChange,
  onClose,
}) => {
  return (
    <Modal open={true}>
      <Header handleClose={onClose}>
        <Trans id="live.listings_page.filters.sort_modal_title" />
      </Header>
      <Body>
        <Flex flexDirection="column-reverse">
          <FilterInputs
            languages={languages}
            instructor={instructor}
            sortBy={sortBy}
            theme={theme}
            onInstructorChange={onInstructorChange}
            onLanguageChange={onLanguageChange}
            onSortChange={onSortChange}
            onThemeChange={onThemeChange}
            programCategory={programCategory}
            isMobile
          />
        </Flex>
      </Body>
    </Modal>
  );
};

const ProgramsFilters = ({
  className = '',
  languages,
  instructor,
  sortBy,
  theme,
  onLanguageChange,
  onInstructorChange,
  onSortChange,
  onThemeChange,
  programCategory = ProgramCategory['Programs'],
}) => {
  const CSStheme = useContext(ThemeContext);
  const [isModalOpen, openModal] = useState(false);

  return (
    <Filters className={className} data-testid="EventsFilter">
      <Container flexWrap="wrap">
        <Media query={`(min-width: ${CSStheme.viewports.tablet}px)`}>
          {matches => {
            if (matches) {
              return (
                <FilterInputs
                  languages={languages}
                  instructor={instructor}
                  sortBy={sortBy}
                  theme={theme}
                  onInstructorChange={onInstructorChange}
                  onLanguageChange={onLanguageChange}
                  onSortChange={onSortChange}
                  onThemeChange={onThemeChange}
                  programCategory={programCategory}
                  isMobile={false}
                />
              );
            } else {
              return (
                <>
                  <MobileFilterOpen
                    onClick={() => {
                      openModal(true);
                    }}
                  >
                    <Icon name="ygb-icon-restart" />{' '}
                    <Trans id="live.listings_page.filters.sort_mobile_label" />
                  </MobileFilterOpen>
                  {isModalOpen ? (
                    <FilterModal
                      languages={languages}
                      instructor={instructor}
                      sortBy={sortBy}
                      theme={theme}
                      onInstructorChange={onInstructorChange}
                      onLanguageChange={onLanguageChange}
                      onSortChange={onSortChange}
                      onThemeChange={onThemeChange}
                      programCategory={programCategory}
                      onClose={() => openModal(false)}
                    />
                  ) : null}
                </>
              );
            }
          }}
        </Media>
      </Container>
    </Filters>
  );
};

export default ProgramsFilters;
